// A folder's notes, under its files: ported from the old Space app (app/src/components/Notes.tsx).
//   view    the notes, cleaned (sanitize.ts); a checklist box ticks without opening the editor, saved at once
//   edit    contentEditable + a small toolbar; Ctrl+S saves, Esc cancels (FolderModal: the first Escape)
//   save    sends the etag it started from; 409 → "someone saved in between — overwrite?"
// Who may edit comes from the server (FolderBundle.canWrite); the server checks again.
import { notesLengthProblem, type FolderBundle, type FolderNotes, type FolderNotesAnswer, type FolderNotesSave } from '@pathonline/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import { HttpError, sendJson, type Note } from '../api.ts';
import { hubDateTime } from '../format.ts';
import { cleanNotes } from './sanitize.ts';
import { folderKey } from './upload.ts';

const enc = encodeURIComponent;

/** Readable on white and on the dark card. */
const COLORS = [
  ['#e11d48', 'Red'],
  ['#ea580c', 'Orange'],
  ['#ca8a04', 'Yellow'],
  ['#16a34a', 'Green'],
  ['#2563eb', 'Blue'],
  ['#7c3aed', 'Violet'],
] as const;

const CHECK_ITEMS = 'ul[data-checklist] > li';

/** The checklist item whose box was clicked. The box is drawn in the item's first 22 px (CSS ::before). */
function clickedBox(e: ReactMouseEvent): HTMLElement | null {
  const li = (e.target as HTMLElement).closest?.('li');
  if (!li || !li.parentElement?.matches('ul[data-checklist]')) return null;
  return e.clientX - li.getBoundingClientRect().left < 22 ? li : null;
}

const toggleDone = (li: HTMLElement) => {
  if (li.dataset.checked === 'true') delete li.dataset.checked;
  else li.dataset.checked = 'true';
};

/** The list the caret is in, inside the editor. */
function listAtCaret(editor: HTMLElement): HTMLUListElement | null {
  const node = window.getSelection()?.anchorNode;
  const el = node instanceof Element ? node : node?.parentElement;
  const ul = el?.closest('ul');
  return ul && editor.contains(ul) ? ul : null;
}

/**
 * The checklist button:
 *   caret in a checklist     → back to a plain bullet list
 *   caret in a bullet list   → becomes a checklist
 *   caret in plain text      → a new checklist
 */
function toggleChecklist(editor: HTMLElement) {
  const ul = listAtCaret(editor);
  if (ul?.dataset.checklist) {
    delete ul.dataset.checklist;
    ul.querySelectorAll('li').forEach((li) => delete li.dataset.checked);
    return;
  }
  if (ul) {
    ul.dataset.checklist = 'true';
    return;
  }
  insertList(editor, true);
}

/**
 * The bullet-list command, without mixing kinds. Chromium joins a new item with a <ul> right next to it,
 * so a checklist and a plain list could merge:
 *
 *   ☐ milk         Bullets on "call"      ☐ milk
 *   call      ──────────────────────▶      ☐ call      ← should be a plain bullet
 *
 * Chromium keeps the old <li> elements, so each one's kind is noted first. Afterwards every <ul> is split
 * into runs of one kind; new items take `checklist`.
 */
function insertList(editor: HTMLElement, checklist: boolean) {
  const kindOf = new Map<Element, boolean>();
  editor.querySelectorAll('ul > li').forEach((li) => kindOf.set(li, li.parentElement?.dataset.checklist === 'true'));

  document.execCommand('insertUnorderedList');

  const selection = window.getSelection();
  const caret = selection?.rangeCount ? selection.getRangeAt(0) : null;
  const at = caret && { node: caret.startContainer, offset: caret.startOffset };

  editor.querySelectorAll('ul').forEach((ul) => {
    let list = ul;
    let kind = (ul.children[0] && kindOf.get(ul.children[0])) ?? checklist;
    for (const child of Array.from(ul.children)) {
      const own = kindOf.get(child) ?? (child.tagName === 'LI' ? checklist : kind);
      if (own !== kind) {
        const next = document.createElement('ul');
        list.after(next);
        list = next;
        kind = own;
      }
      if (list !== ul) list.append(child);
      if (own) list.dataset.checklist = 'true';
      else {
        delete list.dataset.checklist;
        if (child instanceof HTMLElement) delete child.dataset.checked;
      }
    }
  });

  // Moving an item moves the caret out of it. Put it back.
  if (at && selection && at.node.isConnected) {
    const range = document.createRange();
    range.setStart(at.node, Math.min(at.offset, at.node.nodeType === Node.TEXT_NODE ? (at.node.textContent?.length ?? 0) : at.node.childNodes.length));
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

/** The server's refusal (shared notesLengthProblem) and what to do; '' when they fit. */
export function notesTooLong(html: string): string {
  const problem = notesLengthProblem(html);
  return problem && `${problem} Shorten them, or put the text in a file.`;
}

interface Props {
  nodeId: string;
  notes: FolderNotes;
  canEdit: boolean;
  /** Held by the folder: its Escape cancels the editing first. */
  editing: boolean;
  setEditing: (on: boolean) => void;
}

export function Notes({ nodeId, notes, canEdit, editing, setEditing }: Props) {
  const qc = useQueryClient();
  const editor = useRef<HTMLDivElement>(null);
  // The etag the editor started from; a tick sends the one it was drawn from.
  const base = useRef('');
  // A save on its way (a tick, or the editor's after Esc): editing and ticking wait for it, so they
  // start from the saved version and etag.
  const pending = useRef<Promise<void> | null>(null);
  // Counts the editings that ended: a save answered after its editing ended does not speak in the next one.
  const session = useRef(0);
  // A drag that started in the editor (moving its own text).
  const inside = useRef(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<{ by: string; at: string } | null>(null);
  const [say, setSay] = useState<Note | null>(null);
  const shown = useMemo(() => cleanNotes(notes.html), [notes.html]);

  const put = (html: string, etag: string, force = false) => {
    const body: FolderNotesSave = force ? { html, etag, force } : { html, etag };
    return sendJson<FolderNotesAnswer>('PUT', `/api/nodes/${enc(nodeId)}/folder/notes`, body);
  };
  /** The saved notes into the folder; the icons follow when the notes became empty or stopped being so. */
  const stored = async (saved: FolderNotes, before: string) => {
    // A folder GET on its way (an upload, a delete) may have read the notes before this save: its answer would put them back.
    await qc.cancelQueries({ queryKey: folderKey(nodeId) });
    qc.setQueryData<FolderBundle>(folderKey(nodeId), (old) => old && { ...old, notes: saved });
    if (!before !== !saved.html) void qc.invalidateQueries({ queryKey: ['views'] });
  };
  /** Keeps `save` as the one on its way until it ends. */
  const hold = (save: Promise<void>) => {
    const run: Promise<void> = save.finally(() => {
      if (pending.current === run) pending.current = null;
    });
    pending.current = run;
    return run;
  };

  const start = async () => {
    if (!canEdit) return;
    if (pending.current) await pending.current;
    // The cache: after a save it holds the etag the save answered.
    base.current = qc.getQueryData<FolderBundle>(folderKey(nodeId))?.notes.etag ?? notes.etag;
    setConflict(null);
    setSay(null);
    setEditing(true);
  };

  // Fill the editor once, when editing starts: a refetch must not overwrite what is being typed.
  useEffect(() => {
    const el = editor.current;
    if (!editing || !el) {
      // The editing ended (Esc, Cancel, a save): a save still on its way no longer asks anything.
      session.current++;
      setConflict(null);
      return;
    }
    el.innerHTML = shown;
    // Colours and bold as <span style>, which the cleaning keeps (no <font>).
    document.execCommand('styleWithCSS', false, 'true');
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  }, [editing]);

  const submit = async (force: boolean) => {
    const el = editor.current;
    if (!el || saving) return;
    // No text at all (an empty line, empty checklist items): no notes.
    const html = el.textContent?.trim() ? cleanNotes(el.innerHTML) : '';
    // Nothing changed: nothing to send.
    if (!force && html === shown) return setEditing(false);
    const long = notesTooLong(html);
    if (long) return setSay({ bad: true, text: long });
    setSaving(true);
    setSay(null);
    await hold(saveEdit(html, force));
  };
  const saveEdit = async (html: string, force: boolean) => {
    const mine = session.current;
    try {
      const a = await put(html, base.current, force);
      await stored(a.notes, notes.html);
      // Only the editing it came from: not one opened after Esc or Cancel.
      if (session.current === mine) {
        setConflict(null);
        setEditing(false);
      }
      setSay({ bad: false, text: 'Notes saved.' });
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        // The newer notes, to name who saved them; the editor keeps what was typed.
        await qc.invalidateQueries({ queryKey: folderKey(nodeId) });
        const latest = qc.getQueryData<FolderBundle>(folderKey(nodeId))?.notes;
        // Only while this editing is still open: not after Esc or Cancel, not in the next editing.
        if (session.current === mine) setConflict({ by: latest?.by || 'Someone', at: latest?.at ?? '' });
        // The editing ended before the answer: no question, but say that they were not saved.
        else setSay({ bad: true, text: 'Not saved: someone saved these notes in the meantime — here is their version.' });
      } else setSay({ bad: true, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  /** Ticks a box without opening the editor, and saves at once. */
  const tick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const li = clickedBox(e);
    if (!li || !canEdit || pending.current) return;
    e.preventDefault();
    const index = Array.from(e.currentTarget.querySelectorAll(CHECK_ITEMS)).indexOf(li);
    const doc = document.createElement('template');
    doc.innerHTML = shown;
    const target = doc.content.querySelectorAll<HTMLElement>(CHECK_ITEMS)[index];
    if (!target) return;
    toggleDone(target);
    setSay(null);
    void hold(saveTick(cleanNotes(doc.innerHTML), notes));
  };
  const saveTick = async (next: string, from: FolderNotes) => {
    // A refetch on its way would put the version before the tick back, with its old etag.
    await qc.cancelQueries({ queryKey: folderKey(nodeId) });
    qc.setQueryData<FolderBundle>(folderKey(nodeId), (old) => old && { ...old, notes: { ...old.notes, html: next } });
    try {
      await stored((await put(next, from.etag)).notes, from.html);
    } catch (err) {
      void qc.invalidateQueries({ queryKey: folderKey(nodeId) });
      const conflicted = err instanceof HttpError && err.status === 409;
      setSay({ bad: true, text: conflicted ? 'Someone saved these notes in the meantime — here is their version. Tick it again.' : (err as Error).message });
    }
  };

  return (
    <section className="fm-notes" aria-label="Notes">
      <div className="fm-sec-h">
        <h3>Notes</h3>
        {/* No `by` on a shared link's page: "Saved · <when>". */}
        {notes.at && !editing && (
          <span className="fm-stamp">
            Saved{notes.by && ` by ${notes.by}`} · {hubDateTime(notes.at)}
          </span>
        )}
        {canEdit && !editing && shown && (
          <button type="button" className="link fm-edit" onClick={() => void start()}>
            ✎ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div
          className="fm-ed"
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
              e.preventDefault();
              void submit(false);
            }
          }}
        >
          <Toolbar editor={editor} />
          <div
            ref={editor}
            className="rich fm-ed-box"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline
            aria-label="Notes"
            onPaste={(e) => {
              // Plain text only: pasted Word or web HTML would bring its own fonts and colours.
              e.preventDefault();
              document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
            }}
            onDragStart={() => (inside.current = true)}
            onDragEnd={() => (inside.current = false)}
            onDrop={(e) => {
              // Text dragged in from elsewhere comes as plain text too; a move inside the notes stays one.
              // Files go on to the folder (FolderModal uploads them).
              if (inside.current || e.dataTransfer.types.includes('Files')) return;
              e.preventDefault();
              const at = document.caretRangeFromPoint?.(e.clientX, e.clientY);
              if (at) {
                window.getSelection()?.removeAllRanges();
                window.getSelection()?.addRange(at);
              }
              const text = e.dataTransfer.getData('text/plain');
              if (text) document.execCommand('insertText', false, text);
            }}
            onClick={(e) => {
              const li = clickedBox(e);
              if (li) {
                e.preventDefault();
                toggleDone(li);
              }
            }}
            onInput={(e) => {
              // Enter on a done item copies its mark to the new, empty item. An empty item is never done.
              e.currentTarget.querySelectorAll<HTMLElement>(CHECK_ITEMS).forEach((li) => {
                if (li.dataset.checked && !li.textContent?.trim()) delete li.dataset.checked;
              });
            }}
          />
          {conflict && (
            <div className="fm-conflict" role="alert">
              <p>
                <b>{conflict.by}</b> saved these notes in the meantime{conflict.at ? ` (${hubDateTime(conflict.at)})` : ''}. Overwrite their version with yours?
              </p>
              <div className="fm-row">
                <button type="button" className="btn small danger" disabled={saving} onClick={() => void submit(true)}>
                  Overwrite
                </button>
                <button type="button" className="btn small ghost" onClick={() => setConflict(null)}>
                  Keep editing
                </button>
              </div>
            </div>
          )}
          <div className="fm-ed-foot">
            <span className="fm-hint">Ctrl+S saves · Esc cancels</span>
            <button type="button" className="btn small ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="btn small" disabled={saving} onClick={() => void submit(false)}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : shown ? (
        <div
          className={`rich fm-view ${canEdit ? 'can-tick' : ''}`}
          onClick={tick}
          onDoubleClick={canEdit ? () => void start() : undefined}
          dangerouslySetInnerHTML={{ __html: shown }}
        />
      ) : canEdit ? (
        <button type="button" className="fm-add-notes" onClick={() => void start()}>
          ✎ Add notes — a checklist, decisions, where things are…
        </button>
      ) : (
        <p className="fm-none">No notes yet.</p>
      )}
      {say && (
        <p className={`fm-msg ${say.bad ? 'error' : 'ok'}`} role="status">
          {say.text}
        </p>
      )}
    </section>
  );
}

type Tool = { key: string; label: string; text: string; cls?: string; act: (el: HTMLElement) => void };

const TOOLS: Tool[] = [
  { key: 'bold', label: 'Bold (Ctrl+B)', text: 'B', cls: 'b', act: () => document.execCommand('bold') },
  { key: 'italic', label: 'Italic (Ctrl+I)', text: 'I', cls: 'i', act: () => document.execCommand('italic') },
  { key: 'underline', label: 'Underline (Ctrl+U)', text: 'U', cls: 'u', act: () => document.execCommand('underline') },
  { key: 'strikeThrough', label: 'Strike through', text: 'S', cls: 's', act: () => document.execCommand('strikeThrough') },
];
// Bullets must not join a neighbouring checklist.
const LISTS: Tool[] = [
  { key: 'list', label: 'Bullet list', text: '• List', act: (el) => insertList(el, false) },
  { key: 'checklist', label: 'Checklist', text: '☑ Checklist', act: toggleChecklist },
];

function Toolbar({ editor }: { editor: RefObject<HTMLDivElement | null> }) {
  const [on, setOn] = useState<Record<string, boolean>>({});

  // Light up B / I / U / S and the lists for the text under the caret.
  useEffect(() => {
    const read = () => {
      const el = editor.current;
      const ul = el ? listAtCaret(el) : null;
      const next: Record<string, boolean> = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        list: !!ul && !ul.dataset.checklist,
        checklist: !!ul?.dataset.checklist,
      };
      // Most caret moves and keystrokes change no button: no new render then.
      setOn((prev) => (Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next));
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, [editor]);

  // A press keeps the selection in the editor (its mousedown is cancelled); the click then acts on it.
  // From the keyboard the editor takes the focus back first.
  const keep = (e: ReactMouseEvent) => e.preventDefault();
  const act = (f: (el: HTMLElement) => void) => () => {
    const el = editor.current;
    if (!el) return;
    if (document.activeElement !== el) el.focus();
    f(el);
    document.dispatchEvent(new Event('selectionchange'));
  };
  const button = (t: Tool) => (
    <button key={t.key} type="button" className={`fm-tool ${t.cls ?? ''}`} title={t.label} aria-label={t.label} aria-pressed={!!on[t.key]} onMouseDown={keep} onClick={act(t.act)}>
      {t.text}
    </button>
  );

  return (
    <div className="fm-tools" role="toolbar" aria-label="Formatting">
      {TOOLS.map(button)}
      <span className="fm-sep" />
      <span className="fm-colors" role="group" aria-label="Text colour">
        {COLORS.map(([c, name]) => (
          <button
            key={c}
            type="button"
            className="fm-color"
            style={{ background: c }}
            title={name}
            aria-label={`Text colour ${name.toLowerCase()}`}
            onMouseDown={keep}
            onClick={act(() => document.execCommand('foreColor', false, c))}
          />
        ))}
      </span>
      <span className="fm-sep" />
      {LISTS.map(button)}
    </div>
  );
}
